'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { PageHeader } from '@/components/common/page-header';
import { Button } from '@/components/ui/button';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { OIL_CODE_COLORS, OIL_NAME_COLORS, type OilCode } from '@/constants/fuel-colors';
import { useOrganization } from '@/contexts/organization-context';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import {
  PetrohqApiResponsePagedModelSalesInfoDetailsDto,
  PetrohqApiResponseSalesInfoAggregationResponse,
  SalesInfoDetailsDto,
  SalesInfoOilAggregationDto,
  SalesInfoOilSaleTypeAggregationDto,
  SalesInfoSaleTypeAggregationDto,
} from '@/lib/api/data-contracts';
import { useFilterStore } from '@/stores/filter-store';
import { useCallback, useEffect, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';

export function SalesInfo() {
  const { toast } = useToast();
  const { getAllStores } = useOrganization();

  // 온디맨드 방식: API 호출 시점에만 필터 상태 읽기

  // 상태 관리 (API 타입 사용)
  const [salesDetails, setSalesDetails] = useState<SalesInfoDetailsDto[]>([]);
  const [saleTypeAggregation, setSaleTypeAggregation] = useState<
    SalesInfoSaleTypeAggregationDto[]
  >([]);
  const [oilSaleTypeAggregation, setOilSaleTypeAggregation] = useState<
    SalesInfoOilSaleTypeAggregationDto[]
  >([]);
  const [oilAggregation, setOilAggregation] = useState<
    SalesInfoOilAggregationDto[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    details: false,
    saleType: false,
    oil: false,
    oilSaleType: false,
  });

  // 페이징 관련 상태
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalElements, setTotalElements] = useState(0);
  const [, setIsLast] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // 조회 버튼 클릭 핸들러
  const handleSearch = () => {
    fetchSalesData();
  };

  // 데이터 가져오기 함수
  const fetchSalesData = useCallback(
    async (append = false, pageNumber = currentPage) => {
      // API 호출 시점에 현재 필터 상태 읽기
      const filters = useFilterStore.getState();

      if (!filters.dateRange?.start || !filters.dateRange?.end) {
        return;
      }

      setIsLoading(true);
      try {
        const storeCodeList =
          filters.store.length > 0 ? filters.store : getAllStores();

        // 공통 파라미터 구성 함수 - 필터 스토어 메서드 사용으로 간소화
        const buildCommonParams = () => {
          return filters.buildApiParams('salesinfo', storeCodeList);
        };

        // 상세 데이터 API 파라미터 구성
        const detailsParams = buildCommonParams();
        detailsParams.append('page', pageNumber.toString());
        detailsParams.append('size', pageSize.toString());

        // 집계 데이터 API 파라미터 구성 (페이지네이션 제외)
        const aggregationParams = buildCommonParams();

        // API 호출 - append 모드에 따라 다르게 처리
        let detailsResponse, aggregationResponse;

        if (append) {
          // append 모드에서는 상세 데이터만 가져오기
          detailsResponse = await apiClient.get('sales/salesinfo/details', {
            searchParams: detailsParams,
          });
        } else {
          // 초기 로드 시에는 병렬로 모든 데이터 가져오기
          [detailsResponse, aggregationResponse] = await Promise.all([
            apiClient.get('sales/salesinfo/details', {
              searchParams: detailsParams,
            }),
            apiClient.get('sales/salesinfo/aggregation', {
              searchParams: aggregationParams,
            }),
          ]);
        }

        // 상세 데이터 처리
        const detailsData =
          (await detailsResponse.json()) as PetrohqApiResponsePagedModelSalesInfoDetailsDto;
        if (detailsData.result) {
          const newData = detailsData.result.content || [];

          if (append) {
            setSalesDetails(prev => {
              const combined = [...prev, ...newData];
              return combined;
            });
          } else {
            setSalesDetails(newData);
          }

          // 페이징 정보 업데이트
          setTotalElements(detailsData.result.page?.totalElements || 0);
          setIsLast(detailsData.result.page?.number === (detailsData.result.page?.totalPages || 0) - 1);
          setHasMore(
            (detailsData.result.page?.number || 0) <
            (detailsData.result.page?.totalPages || 0) - 1
          );
        }

        // 집계 데이터 처리 (append 모드가 아닐 때만)
        if (!append && aggregationResponse) {
          const aggregationData =
            (await aggregationResponse.json()) as PetrohqApiResponseSalesInfoAggregationResponse;
          if (aggregationData.result) {
            setSaleTypeAggregation(
              aggregationData.result.saleTypeAggregation || []
            );
            setOilSaleTypeAggregation(
              aggregationData.result.oilSaleTypeAggregation || []
            );
            setOilAggregation(aggregationData.result.oilAggregation || []);
          }
        }
      } catch (error) {
        console.error('Sales data fetch error:', error);
        toast({
          title: '조회 실패',
          description: '매출 데이터를 불러오는 중 오류가 발생했습니다',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [getAllStores, toast]
  );

  // 초기 데이터 로드 및 필터 변경 시 리로드
  useEffect(() => {
    // 필터 변경 시 페이지 리셋
    setCurrentPage(0);
    setSalesDetails([]);
    setTotalElements(0);
    setIsLast(false);
    setHasMore(true);
    fetchSalesData();
  }, [fetchSalesData]);

  // 무한 스크롤 - 더 많은 데이터 로드
  const loadMoreData = useCallback(() => {
    console.log(
      'loadMoreData 호출 - currentPage:',
      currentPage,
      'hasMore:',
      hasMore,
      'isLoading:',
      isLoading
    );
    if (!isLoading && hasMore) {
      const nextPage = currentPage + 1;
      console.log('다음 페이지 로드:', nextPage);
      setCurrentPage(nextPage);
      fetchSalesData(true, nextPage);
    }
  }, [isLoading, hasMore, currentPage, fetchSalesData]);

  // 새로고침 핸들러
  const handleRefresh = () => {
    setCurrentPage(0);
    setSalesDetails([]);
    setTotalElements(0);
    setIsLast(false);
    setHasMore(true);
    fetchSalesData();
  };

  // 확장/축소 핸들러
  const handleExpandChange = (gridType: string) => (isExpanded: boolean) => {
    setExpanded(prev => ({
      ...prev,
      [gridType]: isExpanded,
    }));
  };

  // 페이지 크기 변경
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0);
    setSalesDetails([]);
    setTotalElements(0);
    setIsLast(false);
    setHasMore(true);
  };

  return (
    <div>
      <ExpandableCard
        title='매출 상세내역'
        breadcrumb={
          <Breadcrumb items={menuBreadcrumbItems('salesinfo')} />
        }
        pageHeader={
          <PageHeader
            onSearch={handleSearch}
            isLoading={isLoading}
          />
        }
        onExpandChange={handleExpandChange('details')}
      >
        <div
          className='border rounded-lg'
          style={{ height: expanded.details ? '95%' : '530px' }}
        >
          <div className='w-full h-full'>
            <RealGridReact
              gridProps={{
                groupPanel: { visible: false },
                checkBar: { visible: false },
                stateBar: { visible: false },
                edit: { readOnly: true, editable: false },
                sorting: {
                  enabled: true,
                  showSortOrder: true,
                  style: RealGrid.SortStyle.INCLUSIVE,
                },
              }}
              rows={salesDetails}
              onScrollToBottom={loadMoreData}
            >
              <RGDataField
                fieldName={'compCd'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'compName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'storeCd'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'storeName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'closedt'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'closecnt'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'stime'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'saleNo'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'typeName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'pumpNo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'tkCode'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'qty'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'cost'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'amount'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'oilName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'dealName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'carNo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'cardNo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'bigo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'sdate'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'total'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'saleType'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'qtyCost'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'dcAmount'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'saleSeq'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'mdcAmount'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'pdealCode'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'pdealName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'pcarNo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'part'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'sendYn'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataColumn fieldName={'storeName'} header={{ text: '매장' }} />
              <RGDataColumn
                fieldName={'closedt'}
                header={{ text: '영업일' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'closecnt'}
                header={{ text: '차수' }}
                width={50}
              />
              <RGDataColumn
                fieldName={'stime'}
                header={{ text: '판매시간' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'typeName'}
                header={{ text: '판매구분' }}
                width={70}
              />
              <RGDataColumn
                fieldName={'saleNo'}
                header={{ text: '순번' }}
                width={55}
                numberFormat={'#,##0'}
              />
              <RGDataColumn
                fieldName={'pumpNo'}
                header={{ text: '주유기' }}
                width={65}
              />
              <RGDataColumn
                fieldName={'tkCode'}
                header={{ text: '탱크' }}
                width={55}
              />
              <RGDataColumn
                fieldName={'qty'}
                header={{ text: '수량' }}
                width={55}
                numberFormat={'#,##0.000'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0.000' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0.000' }}
              />
              <RGDataColumn
                fieldName={'cost'}
                header={{ text: '단가' }}
                width={70}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'amount'}
                header={{ text: '금액' }}
                width={80}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName={'oilName'}
                header={{ text: '유종' }}
                width={60}
                styleCallback={(_grid, dataCell) => ({
                  style: { backgroundColor: OIL_NAME_COLORS[dataCell.value] },
                })}
              />
              <RGDataColumn
                fieldName={'dealName'}
                header={{ text: '거래처' }}
                width={70}
              />
              <RGDataColumn fieldName={'carNo'} header={{ text: '차량번호' }} />
              <RGDataColumn
                fieldName={'cardNo'}
                header={{ text: '신용카드번호' }}
                width={110}
              />
              <RGDataColumn
                fieldName={'bigo'}
                header={{ text: '비고' }}
                width={70}
              />
              <RGDataColumn
                fieldName={'sdate'}
                header={{ text: '날짜' }}
                width={100}
              />
              <RGDataColumn
                fieldName={'total'}
                header={{ text: '총계' }}
                numberFormat={'#,##0.###'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'saleType'}
                header={{ text: '거래구분' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'qtyCost'}
                header={{ text: '원금액' }}
                width={80}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName={'dcAmount'}
                header={{ text: 'DC금액(교대)' }}
                width={90}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'saleSeq'}
                header={{ text: '거래번호' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'mdcAmount'}
                header={{ text: 'DC금액(일마감)' }}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
              <RGDataColumn fieldName={'pdealCode'} header={{ text: '코드' }} />
              <RGDataColumn
                fieldName={'pdealName'}
                header={{ text: '매출거래처' }}
              />
              <RGDataColumn
                fieldName={'pcarNo'}
                header={{ text: '매출차량' }}
              />
              <RGDataColumn
                fieldName={'part'}
                header={{ text: 'PART' }}
                width={40}
              />
              <RGDataColumn
                fieldName={'sendYn'}
                header={{ text: 'SEND' }}
                width={40}
              />
            </RealGridReact>
          </div>
        </div>

        {/* Item Counter */}
        <div className='mt-4 flex items-center justify-between'>
          <div className='flex items-center space-x-4'>
            <span className='text-sm text-muted-foreground'>
              현재 {salesDetails.length}건 / 총 {totalElements.toLocaleString()}
              건
              {currentPage > 0 && (
                <span className='ml-2 text-xs bg-gray-100 px-2 py-1 rounded'>
                  {currentPage + 1}페이지까지 로드됨
                </span>
              )}
            </span>
            {isLoading && (
              <span className='text-sm text-blue-600'>로딩 중...</span>
            )}
            {hasMore && !isLoading && (
              <span className='text-sm text-orange-600'>
                스크롤하여 더 보기
              </span>
            )}
            {!hasMore && totalElements > 0 && (
              <span className='text-sm text-green-600'>
                모든 데이터 로드 완료
              </span>
            )}
          </div>
          <div className='flex items-center space-x-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={handleRefresh}
              disabled={isLoading}
            >
              새로고침
            </Button>
            {hasMore && (
              <Button
                variant='outline'
                size='sm'
                onClick={loadMoreData}
                disabled={isLoading}
              >
                더 보기
              </Button>
            )}
            <div className='border-l pl-2 flex items-center space-x-2'>
              <span className='text-sm text-muted-foreground'>
                페이지 크기:
              </span>
              {[50, 100, 500].map(size => (
                <Button
                  key={size}
                  variant={pageSize === size ? 'default' : 'outline'}
                  size='sm'
                  onClick={() => handlePageSizeChange(size)}
                  disabled={isLoading}
                >
                  {size}
                </Button>
              ))}
              <Button
                variant={pageSize >= 1000 ? 'default' : 'outline'}
                size='sm'
                onClick={() => handlePageSizeChange(totalElements || 1000)}
                disabled={isLoading}
              >
                전체
              </Button>
            </div>
          </div>
        </div>
      </ExpandableCard>

      {/* Aggregation Grids */}
      <div className='flex flex-col lg:flex-row'>
        {/* Left Column - Sale Type and Oil Aggregation */}
        <div className='flex flex-col lg:w-1/2'>
          {/* Sale Type Aggregation */}
          <ExpandableCard
            title='판매유형별 집계'
            onExpandChange={handleExpandChange('saleType')}
          >
            <div
              className='border rounded-lg'
              style={{ height: expanded.saleType ? '100%' : '200px' }}
            >
              <div className='w-full h-full'>
                <RealGridReact
                  gridProps={{
                    groupPanel: { visible: false },
                    display: { fitStyle: RealGrid.GridFitStyle.FILL },
                    checkBar: { visible: false },
                    stateBar: { visible: false },
                    edit: { readOnly: true, editable: false },
                    sorting: {
                      enabled: true,
                      showSortOrder: true,
                      style: RealGrid.SortStyle.INCLUSIVE,
                    },
                  }}
                  rows={saleTypeAggregation}
                >
                  <RGDataField
                    fieldName={'typeCode'}
                    dataType={RealGrid.ValueType.TEXT}
                  />
                  <RGDataField
                    fieldName={'typeName'}
                    dataType={RealGrid.ValueType.TEXT}
                  />
                  <RGDataField
                    fieldName={'oilKind'}
                    dataType={RealGrid.ValueType.TEXT}
                  />
                  <RGDataField
                    fieldName={'oilKindName'}
                    dataType={RealGrid.ValueType.TEXT}
                  />
                  <RGDataField
                    fieldName={'cnt'}
                    dataType={RealGrid.ValueType.NUMBER}
                  />
                  <RGDataField
                    fieldName={'qty'}
                    dataType={RealGrid.ValueType.NUMBER}
                  />
                  <RGDataField
                    fieldName={'drum'}
                    dataType={RealGrid.ValueType.NUMBER}
                  />
                  <RGDataField
                    fieldName={'amount'}
                    dataType={RealGrid.ValueType.NUMBER}
                  />
                  <RGDataColumn
                    fieldName={'typeName'}
                    header={{ text: '판매구분' }}
                    width={70}
                  />
                  <RGDataColumn
                    fieldName={'oilKind'}
                    header={{ text: '상품구분코드' }}
                    visible={false}
                  />
                  <RGDataColumn
                    fieldName={'oilKindName'}
                    header={{ text: '상품구분' }}
                    groupFooter={{ text: '총계' }}
                  />
                  <RGDataColumn
                    fieldName={'cnt'}
                    header={{ text: '건수' }}
                    width={40}
                    numberFormat={'#,##0'}
                    styleName={'text-right'}
                    footer={{ expression: 'sum', numberFormat: '#,##0' }}
                    groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
                  />
                  <RGDataColumn
                    fieldName={'qty'}
                    header={{ text: '수량' }}
                    numberFormat={'#,##0.000'}
                    styleName={'text-right'}
                    footer={{ expression: 'sum', numberFormat: '#,##0.000' }}
                    groupFooter={{ expression: 'sum', numberFormat: '#,##0.000' }}
                  />
                  <RGDataColumn
                    fieldName={'drum'}
                    header={{ text: 'DRUM/TON' }}
                    width={60}
                    numberFormat={'#,##0.0'}
                    styleName={'text-right'}
                  />
                  <RGDataColumn
                    fieldName={'amount'}
                    header={{ text: '금액' }}
                    numberFormat={'#,##0'}
                    styleName={'text-right'}
                    footer={{ expression: 'sum', numberFormat: '#,##0' }}
                    groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
                  />
                </RealGridReact>
              </div>
            </div>
          </ExpandableCard>

          {/* Oil Aggregation */}
          <ExpandableCard
            title='유종별 집계'
            onExpandChange={handleExpandChange('oil')}
          >
            <div
              className='border rounded-lg'
              style={{ height: expanded.oil ? '100%' : '200px' }}
            >
              <div className='w-full h-full'>
                <RealGridReact
                  gridProps={{
                    groupPanel: { visible: false },
                    display: { fitStyle: RealGrid.GridFitStyle.FILL },
                    checkBar: { visible: false },
                    stateBar: { visible: false },
                    edit: { readOnly: true, editable: false },
                    sorting: {
                      enabled: true,
                      showSortOrder: true,
                      style: RealGrid.SortStyle.INCLUSIVE,
                    },
                  }}
                  rows={oilAggregation}
                >
                  <RGDataField
                    fieldName={'oilCode'}
                    dataType={RealGrid.ValueType.TEXT}
                  />
                  <RGDataField
                    fieldName={'oilName'}
                    dataType={RealGrid.ValueType.TEXT}
                  />
                  <RGDataField
                    fieldName={'oilKind'}
                    dataType={RealGrid.ValueType.TEXT}
                  />
                  <RGDataField
                    fieldName={'oilKindName'}
                    dataType={RealGrid.ValueType.TEXT}
                  />
                  <RGDataField
                    fieldName={'cnt'}
                    dataType={RealGrid.ValueType.NUMBER}
                  />
                  <RGDataField
                    fieldName={'qty'}
                    dataType={RealGrid.ValueType.NUMBER}
                  />
                  <RGDataField
                    fieldName={'drum'}
                    dataType={RealGrid.ValueType.NUMBER}
                  />
                  <RGDataField
                    fieldName={'cost'}
                    dataType={RealGrid.ValueType.NUMBER}
                  />
                  <RGDataField
                    fieldName={'amount'}
                    dataType={RealGrid.ValueType.NUMBER}
                  />
                  <RGDataColumn
                    fieldName={'oilName'}
                    header={{ text: '유종' }}
                    width={70}
                    styleCallback={(grid, dataCell) => {
                      const oilCode = grid.getValue(dataCell.index?.itemIndex!!, 'oilCode');
                      return {
                        style: { backgroundColor: OIL_CODE_COLORS[Number(oilCode) as OilCode] },
                      };
                    }}
                  />
                  <RGDataColumn
                    fieldName={'oilKind'}
                    header={{ text: '상품구분코드' }}
                    visible={false}
                  />
                  <RGDataColumn
                    fieldName={'oilKindName'}
                    header={{ text: '상품구분' }}
                    groupFooter={{ text: '총계' }}
                  />
                  <RGDataColumn
                    fieldName={'cnt'}
                    header={{ text: '건수' }}
                    width={40}
                    numberFormat={'#,##0'}
                    styleName={'text-right'}
                    footer={{ expression: 'sum', numberFormat: '#,##0' }}
                    groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
                  />
                  <RGDataColumn
                    fieldName={'qty'}
                    header={{ text: '수량' }}
                    numberFormat={'#,##0.000'}
                    styleName={'text-right'}
                    footer={{ expression: 'sum', numberFormat: '#,##0.000' }}
                    groupFooter={{ expression: 'sum', numberFormat: '#,##0.000' }}
                  />
                  <RGDataColumn
                    fieldName={'drum'}
                    header={{ text: 'DRUM/TON' }}
                    width={60}
                    numberFormat={'#,##0.0'}
                    styleName={'text-right'}
                  />
                  <RGDataColumn
                    fieldName={'cost'}
                    header={{ text: '단가' }}
                    numberFormat={'#,##0'}
                    styleName={'text-right'}
                  />
                  <RGDataColumn
                    fieldName={'amount'}
                    header={{ text: '금액' }}
                    numberFormat={'#,##0'}
                    styleName={'text-right'}
                    footer={{ expression: 'sum', numberFormat: '#,##0' }}
                    groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
                  />
                </RealGridReact>
              </div>
            </div>
          </ExpandableCard>
        </div>

        {/* Right Column - Oil Sale Type Aggregation */}
        <div className='lg:w-1/2'>
          <ExpandableCard
            title='유종별 판매유형 집계'
            onExpandChange={handleExpandChange('oilSaleType')}
          >
            <div
              className='border rounded-lg'
              style={{ height: expanded.oilSaleType ? '100%' : '540px' }}
            >
              <div className='w-full h-full'>
                <RealGridReact
                  gridProps={{
                    groupPanel: { visible: false },
                    display: { fitStyle: RealGrid.GridFitStyle.FILL },
                    checkBar: { visible: false },
                    stateBar: { visible: false },
                    edit: { readOnly: true, editable: false },
                    sorting: {
                      enabled: true,
                      showSortOrder: true,
                      style: RealGrid.SortStyle.INCLUSIVE,
                    },
                  }}
                  rows={oilSaleTypeAggregation}
                >
                  <RGDataField
                    fieldName={'typeCode'}
                    dataType={RealGrid.ValueType.TEXT}
                  />
                  <RGDataField
                    fieldName={'typeName'}
                    dataType={RealGrid.ValueType.TEXT}
                  />
                  <RGDataField
                    fieldName={'oilCode'}
                    dataType={RealGrid.ValueType.TEXT}
                  />
                  <RGDataField
                    fieldName={'oilName'}
                    dataType={RealGrid.ValueType.TEXT}
                  />
                  <RGDataField
                    fieldName={'oilKind'}
                    dataType={RealGrid.ValueType.TEXT}
                  />
                  <RGDataField
                    fieldName={'oilKindName'}
                    dataType={RealGrid.ValueType.TEXT}
                  />
                  <RGDataField
                    fieldName={'cnt'}
                    dataType={RealGrid.ValueType.NUMBER}
                  />
                  <RGDataField
                    fieldName={'qty'}
                    dataType={RealGrid.ValueType.NUMBER}
                  />
                  <RGDataField
                    fieldName={'drum'}
                    dataType={RealGrid.ValueType.NUMBER}
                  />
                  <RGDataField
                    fieldName={'amount'}
                    dataType={RealGrid.ValueType.NUMBER}
                  />
                  <RGDataColumn
                    fieldName={'typeName'}
                    header={{ text: '판매구분' }}
                    width={70}
                  />
                  <RGDataColumn
                    fieldName={'oilName'}
                    header={{ text: '유종' }}
                    width={70}
                    styleCallback={(grid, dataCell) => {
                      const oilCode = grid.getValue(dataCell.index?.itemIndex!!, 'oilCode');
                      return {
                        style: { backgroundColor: OIL_CODE_COLORS[Number(oilCode) as OilCode] },
                      };
                    }}
                  />
                  <RGDataColumn
                    fieldName={'oilKind'}
                    header={{ text: '상품구분코드' }}
                    visible={false}
                  />
                  <RGDataColumn
                    fieldName={'oilKindName'}
                    header={{ text: '상품구분' }}
                    groupFooter={{ text: '총계' }}
                  />
                  <RGDataColumn
                    fieldName={'cnt'}
                    header={{ text: '건수' }}
                    width={40}
                    numberFormat={'#,##0'}
                    styleName={'text-right'}
                    footer={{ expression: 'sum', numberFormat: '#,##0' }}
                    groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
                  />
                  <RGDataColumn
                    fieldName={'qty'}
                    header={{ text: '수량' }}
                    numberFormat={'#,##0'}
                    styleName={'text-right'}
                  />
                  <RGDataColumn
                    fieldName={'drum'}
                    header={{ text: 'DRUM/TON' }}
                    width={60}
                    numberFormat={'#,##0.0'}
                    styleName={'text-right'}
                  />
                  <RGDataColumn
                    fieldName={'amount'}
                    header={{ text: '금액' }}
                    numberFormat={'#,##0'}
                    styleName={'text-right'}
                    footer={{ expression: 'sum', numberFormat: '#,##0' }}
                    groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
                  />
                </RealGridReact>
              </div>
            </div>
          </ExpandableCard>
        </div>
      </div>
    </div>
  );
}
